/**
 * Additional Coverages — dollar inputs that stack on top of the
 * Adjusted Premium to produce Total Premium.
 */
import { FilePlus02 } from "@untitledui/icons";
import { FormGroup } from "../../../../components/form-group";
import { MoneyInput } from "../../../../components/money-input";
import type { PremiumSelectionDerived, PremiumSelectionInputs } from "../../../../domain/pricing";
import { CardRows, ComputedRow, FieldRow } from "../components/field-row";
import { useBlurFlash } from "../components/use-blur-flash";
import { clampMoneyToWire, displayMoneyValue } from "../premium-selection-types";
import { FIELD_TOOLTIPS } from "../tooltips";
import type { InputKey, PendingMap } from "../use-premium-selection";

const COVERAGES: Array<{ key: InputKey; label: string; dataAutotest: string }> = [
    { key: "punitiveDamages", label: "Punitive Damages", dataAutotest: "premium-selection-additional-coverages-punitive-damages-row" },
    { key: "defenseInAddition", label: "Defense In Addition", dataAutotest: "premium-selection-additional-coverages-defense-in-addition-row" },
    { key: "abuse", label: "Abuse", dataAutotest: "premium-selection-additional-coverages-abuse-row" },
    { key: "hnoa", label: "HNOA", dataAutotest: "premium-selection-additional-coverages-hnoa-row" },
];

interface AdditionalCoveragesCardProps {
    inputs: PremiumSelectionInputs;
    derived: PremiumSelectionDerived | null;
    pending: PendingMap;
    setInput: (key: InputKey, value: string) => void;
    commit: (key: InputKey) => void;
}

export function AdditionalCoveragesCard({ inputs, derived, pending, setInput, commit }: AdditionalCoveragesCardProps) {
    return (
        <FormGroup title="Additional Coverages" icon={FilePlus02} data-autotest="premium-selection-additional-coverages-card">
            <CardRows>
                {COVERAGES.map(({ key, label, dataAutotest }) => (
                    <CoverageRow
                        key={key}
                        inputKey={key}
                        label={label}
                        value={inputs[key]}
                        setInput={setInput}
                        commit={commit}
                        dataAutotest={dataAutotest}
                    />
                ))}
                <ComputedRow
                    label="Total Premium"
                    tooltip={FIELD_TOOLTIPS.totalPremium}
                    value={displayMoneyValue(derived?.totalPremium ?? null)}
                    isPending={Boolean(pending.totalPremium) || derived === null}
                    highlighted
                    data-autotest="premium-selection-additional-coverages-total-premium-row"
                />
            </CardRows>
        </FormGroup>
    );
}

function CoverageRow({
    inputKey,
    label,
    value,
    setInput,
    commit,
    dataAutotest,
}: {
    inputKey: InputKey;
    label: string;
    value: string;
    setInput: (key: InputKey, value: string) => void;
    commit: (key: InputKey) => void;
    dataAutotest: string;
}) {
    const { flashKey, flash } = useBlurFlash();
    return (
        <FieldRow label={label} flashKey={flashKey} data-autotest={dataAutotest}>
            <MoneyInput
                aria-label={label}
                value={value}
                onChange={(raw) => setInput(inputKey, raw)}
                onBlur={() => {
                    // Keep the PUT inside the wire's Numeric(14,2) range.
                    const clamped = clampMoneyToWire(value);
                    if (clamped !== value) setInput(inputKey, clamped);
                    commit(inputKey);
                    flash();
                }}
            />
        </FieldRow>
    );
}
